import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { axiosPrivate } from '../../modules/networkTools/networkTools';

type ClinicalEncounterSource = 'Manual' | 'Updated' | 'Data Feed';

export type ClinicalEncountersItem = {
  id?: number;
  dateOfEncounter?: string | null;
  timeOfEncounter?: string | null;
  location?: string | null;
  type?: string | null;
  typeId?: number;
  provider?: string | null;
  reason?: string | null;
  status?: string | null;
  statusId?: number;
  source?: ClinicalEncounterSource;
};

export type ClinicalEncountersFilters = {
  typeId?: string | string[] | null;
  statusId?: string | string[] | null;
  startDate?: string | null;
  endDate?: string | null;
};

export const getClinicalEncountersPreview = createAsyncThunk(
  'clinicalEncounters/getClinicalEncountersPreview',
  async (
    {
      clientId
    }: {
      clientId: string | number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.get(`client/${clientId}/clinical-encounters/preview`);
      return response;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getClinicalEncounters = createAsyncThunk(
  'clinicalEncounters/getClinicalEncounters',
  async (
    {
      clientId,
      page,
      size,
      sorts,
      filters
    }: {
      clientId: string | number;
      page: number;
      size: number;
      sorts: { title: string; direction: string }[] | null;
      filters: ClinicalEncountersFilters | null;
    },
    { rejectWithValue }
  ) => {
    try {
      let params: string | string[] = '';
      if (Array.isArray(sorts) && sorts.length > 0) {
        params = sorts
          .filter((item) => item.direction && item.title)
          .map((item) =>
            item.title === 'dateTime'
              ? `&sort=dateOfEncounter,${item.direction}&sort=timeOfEncounter,${item.direction}`
              : `&sort=${item.title},${item.direction}`
          )
          .join('');
      }

      if (filters) {
        const filterParams = Object.entries(filters)
          .filter(([_, value]) => value != null && value !== '')
          .map(([key, value]) => `&${key}=${encodeURIComponent(String(value))}`)
          .join('');
        params += filterParams;
      }

      const response = await axiosPrivate.get(
        `client/${clientId}/clinical-encounters?page=${page}&size=${size}${params}`
      );
      return { content: response.data, page, size, sorts, filters };
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const postClinicalEncounters = createAsyncThunk(
  'clinicalEncounters/postClinicalEncounters',
  async (
    { clientId, data }: { clientId: string | number; data: ClinicalEncountersItem },
    { rejectWithValue }
  ) => {
    try {
      await axiosPrivate.post(`client/${clientId}/clinical-encounters`, data);
      return data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const putClinicalEncounters = createAsyncThunk(
  'clinicalEncounters/putClinicalEncounters',
  async (
    {
      clientId,
      encounterId,
      data
    }: {
      clientId: string | number;
      encounterId: string | number;
      data: ClinicalEncountersItem | any;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.put(
        `client/${clientId}/clinical-encounters/${encounterId}`,
        data
      );
      return response;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const deleteClinicalEncounters = createAsyncThunk(
  'clinicalEncounters/deleteClinicalEncounters',
  async (
    {
      clientId,
      encounterId
    }: {
      clientId: string | number;
      encounterId: string | number;
    },
    { rejectWithValue }
  ) => {
    try {
      await axiosPrivate.delete(`client/${clientId}/clinical-encounters/${encounterId}`);

      return encounterId;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getClinicalEncountersTypes = createAsyncThunk(
  'clinicalEncounters/getClinicalEncountersTypes',
  async ({ clientId }: { clientId: string | number }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`client/${clientId}/clinical-encounters/types`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getClinicalEncountersStatuses = createAsyncThunk(
  'clinicalEncounters/getClinicalEncountersStatuses',
  async ({ clientId }: { clientId: string | number }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`client/${clientId}/clinical-encounters/statuses`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);
